import React, { useState, useEffect, useCallback } from 'react';
import FlyShieldForm from './components/FlyShieldForm';
import ObservabilidadeFlyshield from './components/observabilidade/ObservabilidadeFlyshield';
import { AuthProvider, useAuth } from './context/AuthContext';
import { userManager } from './services/oidc';
import { ENVIRONMENTS, getCurrentEnvironment } from './config/environmentConfig';
import HomePage from './components/HomePage';
import BorerControlForm from './components/borercontrol/BorerControlForm';
import {
  BORER_ENVIRONMENTS,
  getCurrentBorerEnvironment,
} from './config/borerEnvironmentConfig';
import ObservabilidadeBorerControl from './components/borercontrol/ObservabilidadeBorerControl';

// Flag de módulo para impedir que o callback OIDC seja processado duas vezes
// em React 18 StrictMode (que executa useEffect com [] duas vezes em dev).
// Códigos OAuth são single-use; a 2ª chamada falha com invalid_grant.
let callbackProcessed = false;

const ENV_BADGE_STYLES = {
  producao: { backgroundColor: '#198754', color: '#fff' },
  staging: { backgroundColor: '#ffc107', color: '#000' },
  desenvolvimento: { backgroundColor: '#fd7e14', color: '#fff' },
};

const SCREENS = {
  upload: 'upload',
  observabilidade: 'observabilidade',
};

// ---------------------------------------------------------------------------
// AppContent — renderizado dentro do AuthProvider
// ---------------------------------------------------------------------------

function AppContent() {
  const { isAuthenticated, loading, accessDenied, loggingOut, login, logout: _logout } = useAuth();

  const logout = useCallback(() => {
    setSelectedSystem(null);
    _logout();
  }, [_logout]);
  const [activeScreen, setActiveScreen] = useState(SCREENS.upload);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [currentEnv, setCurrentEnv] = useState(getCurrentEnvironment());
  const [currentBorerEnv, setCurrentBorerEnv] = useState(getCurrentBorerEnvironment());
  const BORER_SCREENS = { upload: 'upload', observabilidade: 'observabilidade' };
  const [activeBorerScreen, setActiveBorerScreen] = useState(BORER_SCREENS.upload);
  const [redirectStuck, setRedirectStuck] = useState(false);

  // Processar callback OIDC e silent renew
  useEffect(() => {
    const path = window.location.pathname;

    if (path === '/callback') {
      // Guard contra dupla execução em StrictMode (códigos OAuth são single-use).
      if (callbackProcessed) {
        return;
      }
      callbackProcessed = true;

      userManager.signinRedirectCallback()
        .then(() => {
          window.history.replaceState({}, document.title, '/');
        })
        .catch((error) => {
          // Reset para permitir retry se necessário (mas vamos sair via window.location.href)
          callbackProcessed = false;
          console.error('[Callback] Erro ao processar callback OIDC:', error);
          userManager.removeUser().finally(() => {
            window.location.href = '/';
          });
        });
      return;
    }

    if (path === '/silent-renew') {
      userManager.signinSilentCallback().catch(console.error);
      return;
    }
  }, []);

  // Disparar login fora do render para evitar chamadas múltiplas a signinRedirect (R1).
  // Também monitora travamento na tela de redirecionamento (R5).
  useEffect(() => {
    const path = window.location.pathname;
    if (loading || accessDenied || loggingOut || isAuthenticated) {
      setRedirectStuck(false);
      return;
    }
    if (path === '/callback' || path === '/silent-renew') {
      return;
    }
    // Iniciar redirect (idempotente — guard interno em login())
    login();
    // Fallback: se em 10s ainda não navegou, mostrar botão de retry
    const timer = setTimeout(() => setRedirectStuck(true), 10000);
    return () => clearTimeout(timer);
  }, [loading, accessDenied, loggingOut, isAuthenticated, login]);

  function handleSystemSelect(systemId) {
    setSelectedSystem(systemId);
  }

  // --- Telas de estado de autenticação ---

  if (loading || window.location.pathname === '/callback' || window.location.pathname === '/silent-renew') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Autenticando...</p>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: '16px', textAlign: 'center', padding: '24px' }}>
        <h2>Acesso Negado</h2>
        <p>Seu usuário não possui permissão para acessar este sistema.</p>
        <p>É necessário ter a role <strong>agroverita_observability</strong> atribuída no Identity Server.</p>
        <button className="btn btn-outline-danger" onClick={logout}>Sair</button>
      </div>
    );
  }

  if (loggingOut) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Saindo...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: '16px' }}>
        <p>Redirecionando para login...</p>
        {redirectStuck && (
          <>
            <p style={{ color: '#dc3545', maxWidth: '480px', textAlign: 'center' }}>
              O redirecionamento está demorando mais que o esperado. Pode haver um problema com o servidor de identidade ou cookies bloqueados.
            </p>
            <button className="btn btn-outline-primary" onClick={() => window.location.reload()}>
              Tentar novamente
            </button>
          </>
        )}
      </div>
    );
  }

  // --- Página Home (sem sistema selecionado) ---
  if (!selectedSystem) {
    return (
      <div className="App">
        <nav className="navbar navbar-dark bg-dark px-3">
          <span className="navbar-brand mb-0 h1">Agroverita Observabilidade</span>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-sm btn-outline-light ms-2" onClick={logout}>
              Sair
            </button>
          </div>
        </nav>
        <HomePage onSelectSystem={handleSystemSelect} />
      </div>
    );
  }

  // --- Aplicação principal — FlyShield (estrutura original preservada) ---
  if (selectedSystem === 'flyshield') {
    return (
      <div className="App">
        <nav className="navbar navbar-dark bg-dark px-3">
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-outline-light me-2"
              onClick={() => { setSelectedSystem(null); setActiveScreen(SCREENS.upload); }}
            >
              ← Início
            </button>
            <span className="navbar-brand mb-0 h1">FlyShield</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="badge" style={ENV_BADGE_STYLES[currentEnv]}>
              {ENVIRONMENTS[currentEnv].label}
            </span>
            <button className="btn btn-sm btn-outline-light ms-2" onClick={logout}>
              Sair
            </button>
          </div>
        </nav>

        <div className="container-fluid mt-3">
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                className={`nav-link${activeScreen === SCREENS.upload ? ' active' : ''}`}
                onClick={() => setActiveScreen(SCREENS.upload)}
              >
                Upload de Imagens
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link${activeScreen === SCREENS.observabilidade ? ' active' : ''}`}
                onClick={() => setActiveScreen(SCREENS.observabilidade)}
              >
                Observabilidade Flyshield
              </button>
            </li>
          </ul>

          {activeScreen === SCREENS.upload && <FlyShieldForm />}
          {activeScreen === SCREENS.observabilidade && <ObservabilidadeFlyshield />}
        </div>
      </div>
    );
  }

  if (selectedSystem === 'borercontrol') {
    return (
      <div className="App">
        <nav className="navbar navbar-dark bg-dark px-3">
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-outline-light me-2"
              onClick={() => setSelectedSystem(null)}
            >
              ← Início
            </button>
            <span className="navbar-brand mb-0 h1">Broca Control</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="badge" style={{ backgroundColor: '#198754', color: '#fff' }}>
              {BORER_ENVIRONMENTS[currentBorerEnv].label}
            </span>
            <button className="btn btn-sm btn-outline-light ms-2" onClick={logout}>
              Sair
            </button>
          </div>
        </nav>

        <div className="container-fluid mt-3">
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                className={`nav-link${activeBorerScreen === BORER_SCREENS.upload ? ' active' : ''}`}
                onClick={() => setActiveBorerScreen(BORER_SCREENS.upload)}
              >
                Upload de Imagem
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link${activeBorerScreen === BORER_SCREENS.observabilidade ? ' active' : ''}`}
                onClick={() => setActiveBorerScreen(BORER_SCREENS.observabilidade)}
              >
                Observabilidade Broca Control
              </button>
            </li>
          </ul>

          {activeBorerScreen === BORER_SCREENS.upload && <BorerControlForm />}
          {activeBorerScreen === BORER_SCREENS.observabilidade && <ObservabilidadeBorerControl />}
        </div>
      </div>
    );
  }

  // Fallback (sistema selecionado desconhecido)
  return null;
}

// ---------------------------------------------------------------------------
// App — envolve tudo no AuthProvider
// ---------------------------------------------------------------------------

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
